import * as Sentry from "@sentry/react";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { ListCard, ListRow } from "@/components/ui/list-card";
import { MetaLine } from "@/components/ui/meta-line";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

import { LawFirmsAPI } from "../../../api/lawFirms";
import type {
  ClassicTemplateAdminEntry,
  LawFirm
} from "../../../api/types/lawFirm";

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
};

/** "legal_brief" → "Legal brief". */
const draftTypeLabel = (value: string): string => {
  const text = value.replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

/**
 * The firm's Classic templates, each with its Follow Case-File Folders
 * switch. Saves on toggle, like the Classic switch.
 */
const ClassicTemplateRows: FC<{ lawFirmId: string }> = ({ lawFirmId }) => {
  const [templates, setTemplates] = useState<
    ClassicTemplateAdminEntry[] | null
  >(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setTemplates(null);
    LawFirmsAPI.listClassicTemplates(lawFirmId)
      .then((rows) => {
        if (live) setTemplates(rows);
      })
      .catch((error) => {
        Sentry.captureException(error);
        if (live) setTemplates([]);
        toast.error("Couldn't load this firm's Classic templates.");
      });
    return () => {
      live = false;
    };
  }, [lawFirmId]);

  const setFollow = (id: string, follow: boolean) =>
    setTemplates((rows) =>
      (rows ?? []).map((row) =>
        row.id === id ? { ...row, follow_folders: follow } : row
      )
    );

  const toggle = async (template: ClassicTemplateAdminEntry, next: boolean) => {
    setSaving(template.id);
    setFollow(template.id, next);
    try {
      const saved = await LawFirmsAPI.setClassicTemplateFollowFolders(
        lawFirmId,
        template.id,
        next
      );
      setFollow(template.id, saved.follow_folders);
      toast.success(
        saved.follow_folders
          ? "This template follows case-file folders"
          : "This template follows the categories"
      );
    } catch (error) {
      Sentry.captureException(error);
      setFollow(template.id, !next);
      toast.error("Couldn't change this template.");
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <ListRow
        title="Follow Case-File Folders"
        description="Briefs from a template that's on name and group exhibits by the case's folders instead of the categories. Which sections apply still follows the categories."
      />
      {templates === null ? (
        <div className="px-4 py-3">
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : templates.length === 0 ? (
        <div className="px-4 py-2.5 text-sm text-fg-3">
          This firm has no Classic templates from the old app.
        </div>
      ) : (
        templates.map((template) => {
          const name = template.name || "Untitled template";
          return (
            <ListRow
              key={template.id}
              title={name}
              description={
                <MetaLine
                  parts={[
                    draftTypeLabel(template.draft_type),
                    template.visa,
                    template.ready ? null : template.reason
                  ]}
                />
              }
              control={
                <Switch
                  aria-label={`Follow Case-File Folders: ${name}`}
                  checked={template.follow_folders}
                  disabled={saving === template.id}
                  onCheckedChange={(next) => void toggle(template, next)}
                />
              }
            />
          );
        })
      )}
    </>
  );
};

/**
 * The Classic drafting allowlist switch, and while it's on, the firm's Classic
 * templates. Each saves on toggle through its own superadmin route; neither
 * is part of the firm's Save.
 */
export const LegacyDraftingCard: FC<{ lawFirm: LawFirm }> = ({ lawFirm }) => {
  const [enabled, setEnabled] = useState(false);
  const [enabledAt, setEnabledAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setEnabled(Boolean(lawFirm.legacy_drafting_enabled));
    setEnabledAt(lawFirm.legacy_drafting_enabled_at ?? null);
  }, [lawFirm]);

  const toggle = async (next: boolean) => {
    setSaving(true);
    setEnabled(next);
    try {
      const saved = await LawFirmsAPI.setLegacyDrafting(lawFirm.uid, next);
      setEnabled(saved.legacy_drafting_enabled);
      if (saved.legacy_drafting_enabled_at) {
        setEnabledAt(saved.legacy_drafting_enabled_at);
      }
      toast.success(
        next ? "Classic drafting is on" : "Classic drafting is off"
      );
    } catch (error) {
      Sentry.captureException(error);
      setEnabled(!next);
      toast.error("Couldn't change Classic drafting for this firm.");
    } finally {
      setSaving(false);
    }
  };

  const since = enabled ? formatDate(enabledAt) : null;
  return (
    <ListCard>
      <ListRow
        title="Classic Drafting"
        description="Offers Classic on new drafts that have a classic template, including the firm's templates and letterheads from the old app."
        control={
          <Switch
            aria-label="Classic Drafting"
            checked={enabled}
            disabled={saving}
            onCheckedChange={(next) => void toggle(next)}
          />
        }
      />
      {enabled ? <ClassicTemplateRows lawFirmId={lawFirm.uid} /> : null}
      <div className="flex items-center gap-2 px-4 py-2.5 text-sm text-fg-3">
        <ShieldCheck className="size-3.5 shrink-0" />
        <span>
          Only CaseBlink superadmins can change this.
          {since ? ` Turned on ${since}.` : ""}
        </span>
      </div>
    </ListCard>
  );
};
